import React from 'react';
import { Box } from '@mui/material';
import WidgetPreview from './WidgetPreview';
import { ZmanimWidget } from '../../business/ZmanimWidget/ZmanimWidget';
import { WeatherWidget } from '../../business/WeatherWidget/WeatherWidget';
import { ShabbatWidget } from '../../business/ShabbatWidget/ShabbatWidget';
import { TextWidget } from '../../business/TextWidget/TextWidget';
import { NoticeBoardWidget } from '../../business/NoticeBoardWidget/NoticeBoardWidget';
import { EmergencyWidget } from '../../business/EmergencyWidget/EmergencyWidget';
import { ContactsWidget } from '../../business/ContactsWidget/ContactsWidget';
import { SimchaWidget } from '../../business/SimchaWidget/SimchaWidget';
import { NitzavSpotlightWidget } from '../../business/NitzavSpotlightWidget/NitzavSpotlightWidget';
import { ExpensesAnalyticsWidget } from '../../business/ExpensesAnalyticsWidget/ExpensesAnalyticsWidget';
import { FinancialGaugeWidget } from '../../business/FinancialGaugeWidget/FinancialGaugeWidget';
import { ImageWidget } from '../../business/ImageWidget/ImageWidget';
import { MoadWidget } from '../../business/MoadWidget/MoadWidget';
import { TefilotWidget } from '../../business/TefilotWidget/TefilotWidget';
import { ParnasWidget } from '../../business/ParnasWidget/ParnasWidget';
import { ClockWidget } from '../../business/ClockWidget/ClockWidget';
import { OmerWidget } from '../../business/OmerWidget/OmerWidget';
import { HalachicTimesWidget } from '../../business/HalachicTimesWidget/HalachicTimesWidget';
import AutoScaler from '../../business/AutoScaler/AutoScaler';
import {
    dummyZmanimData,
    dummyWeatherData,
    dummyShabbatData,
    dummyMoadData,
    dummyTefilotData,
    dummyParnasData,
    dummyOmerData
} from './dummyData';

/**
 * קומפוננטה להצגת תצוגה מקדימה של ווידג'ט עם התוכן האמיתי שלו
 * מקבלת את כל ה-props וה-settings של הווידג'ט ומציגה אותו כמו שהוא באמת
 */
const RealWidgetPreview = ({ widget, scale = 0.15, catalog = [] }) => {
    const { componentKey, props = {}, widgetSettings = {}, templateId } = widget;

    // אם אין componentKey, לא נוכל להציג כלום
    if (!componentKey) {
        return (
            <WidgetPreview scale={scale}>
                <Box sx={{ p: 2, textAlign: 'center', color: '#64748b', bgcolor: 'white' }}>
                    רכיב לא זמין
                </Box>
            </WidgetPreview>
        );
    }

    // מציאת הווידג'ט בקטלוג כדי לקבל את שם הטמפלייט
    const catalogWidget = catalog.find(c => c.componentKey === componentKey);

    // קביעת הטמפלייט - צריך למצוא את השם לפי ה-ID
    let templateName = 'Fixed'; // ברירת מחדל

    if (catalogWidget && catalogWidget.templates) {
        // אם יש templateId, נמצא את השם שלו
        if (templateId) {
            const foundTemplate = catalogWidget.templates.find(t => t.id === templateId);
            if (foundTemplate) {
                templateName = foundTemplate.name;
            }
        }
        // אם יש template בשם (לא ID), נשתמש בו
        else if (widgetSettings.template && !widgetSettings.template.startsWith('template-')) {
            templateName = widgetSettings.template;
        }
        // אחרת, ניקח את הטמפלייט הראשון
        else if (catalogWidget.templates.length > 0) {
            templateName = catalogWidget.templates[0].name;
        }
    }

    const bgColor = widgetSettings.bgColor || '#ffffff';
    // title ו-showTitle יכולים להיות ב-props או ב-widgetSettings
    const title = props.title || widgetSettings.title || '';
    const showTitle = props.showTitle !== undefined ? props.showTitle :
        (widgetSettings.showTitle !== undefined ? widgetSettings.showTitle : true);

    // Debug logs
    console.log('🎨 RealWidgetPreview:', {
        componentKey,
        templateName,
        templateId,
        'widgetSettings.template': widgetSettings.template,
        hasProps: Object.keys(props).length > 0,
        catalogWidget: !!catalogWidget
    });

    const renderWidget = () => {
        switch (componentKey) {
            case 'ZmanimWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <ZmanimWidget
                                zmanim={dummyZmanimData.zmanim}
                                shabbat={dummyZmanimData.shabbat}
                                hebrewDate={dummyZmanimData.hebrewDate}
                                loading={false}
                                error={null}
                                customTimes={props.customTimes || []}
                                shabbatCustomTimes={props.shabbatCustomTimes || []}
                                template={templateName}
                                title={title}
                                showTitle={showTitle}
                                location={props.location || { lat: 32.0853, lng: 34.7818 }}
                                visibleZmanim={props.visibleZmanim || null}
                                dispatch={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'WeatherWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <WeatherWidget
                                weather={dummyWeatherData.weather}
                                loading={false}
                                error={null}
                                template={templateName}
                                location={props.location || { lat: 32.0853, lng: 34.7818 }}
                                cityName={props.cityName || 'תל אביב'}
                                title={title}
                                showTitle={showTitle}
                                dispatch={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'ShabbatWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <ShabbatWidget
                                shabbat={dummyShabbatData.shabbat}
                                loading={false}
                                error={null}
                                customTimes={props.customTimes || []}
                                rabbiName={typeof props.rabbiName === 'string' ? props.rabbiName : (props.rabbiName?.name || '')}
                                template={templateName}
                                title={title}
                                showTitle={showTitle}
                                location={props.location || { lat: 32.0853, lng: 34.7818 }}
                                dispatch={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'TextWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <TextWidget
                                {...props}
                                template={templateName}
                                onSave={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'NoticeBoardWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <NoticeBoardWidget
                                {...props}
                                template={templateName}
                                title={title}
                                showTitle={showTitle}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'EmergencyWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <EmergencyWidget
                                initialData={props.data}
                                template={templateName}
                                title={title}
                                showTitle={showTitle}
                                onSave={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'ContactsWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <ContactsWidget
                                initialData={props.data}
                                template={templateName}
                                title={title}
                                showTitle={showTitle}
                                onSave={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'SimchaWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <SimchaWidget
                                initialData={props.data}
                                template={templateName}
                                title={title}
                                showTitle={showTitle}
                                onSave={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'NitzavSpotlightWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <NitzavSpotlightWidget
                                data={props.data}
                                title={title}
                                showTitle={showTitle}
                                onSave={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'ExpensesAnalyticsWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <ExpensesAnalyticsWidget
                                data={props.data}
                                freeText={props.freeText}
                                template={templateName}
                                title={title}
                                showTitle={showTitle}
                                onSave={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'FinancialGaugeWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <FinancialGaugeWidget
                                data={props.data}
                                template={templateName}
                                title={title}
                                showTitle={showTitle}
                                onSave={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'ImageWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <ImageWidget
                                imageUrl={props.imageUrl}
                                template={templateName}
                                onSave={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'MoadWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <MoadWidget
                                moadData={dummyMoadData.moadData}
                                customTimes={props.customTimes || dummyMoadData.customTimes}
                                loading={false}
                                error={null}
                                days={[]}
                                template={templateName}
                                title={title || 'מועד'}
                                showTitle={showTitle}
                                widgetId="preview"
                                location={props.location || { lat: 32.0853, lng: 34.7818 }}
                                dispatch={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'TefilotWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <TefilotWidget
                                tefilotData={dummyTefilotData.tefilotData}
                                customTimes={props.customTimes || dummyTefilotData.customTimes}
                                loading={false}
                                error={null}
                                recurringDisplay={props.recurringDisplay || 'compact'}
                                showRuleMode={props.showRuleMode || false}
                                highlightNextMinyan={props.highlightNextMinyan || false}
                                template={templateName}
                                title={title || 'תפילות יום חול'}
                                showTitle={showTitle}
                                widgetId="preview"
                                location={props.location || { lat: 32.0853, lng: 34.7818 }}
                                dispatch={() => { }}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'ParnasWidget': {
                const activeSponsors = props.activeSponsors || [
                    { sponsorName: 'משפחת כהן',        slotType: 'daily',   note: 'לעילוי נשמת אבינו מורינו' },
                    { sponsorName: 'משפחת לוי',        slotType: 'weekly',  note: '' },
                    { sponsorName: "ר' ישראל ישראלי",  slotType: 'monthly', note: 'לרפואת שלמה' },
                ];
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <ParnasWidget
                                parnasData={{
                                    activeSponsors,
                                    currentParshaName: 'ויקרא',
                                    todayHebrewLabel: "א' בניסן תשפ\"ו",
                                }}
                                sponsors={props.sponsors || dummyParnasData.sponsors}
                                parnasType={props.parnasType || dummyParnasData.parnasType}
                                parnasTypeCustomLabel={props.parnasTypeCustomLabel || ''}
                                parnasTypeIcon={props.parnasTypeIcon || dummyParnasData.parnasTypeIcon}
                                tileColors={props.tileColors}
                                placeholderName={props.placeholderName}
                                placeholderNote={props.placeholderNote}
                                template={templateId || 'Tiles'}
                                title={title}
                                showTitle={showTitle}
                                dispatch={() => {}}
                            />
                        </AutoScaler>
                    </Box>
                );
            }

            case 'ClockWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <ClockWidget
                                template={templateName}
                                bgColor={props.bgColor}
                                textColor={props.textColor}
                                fontFamily={props.fontFamily}
                                accentColor={props.accentColor}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'OmerWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor }}>
                        <AutoScaler>
                            <OmerWidget
                                widgetId="preview"
                                previewOmerDay={dummyOmerData.omerDay}
                                loading={false}
                                error={null}
                                dispatch={() => {}}
                                location={null}
                                nusach={props.nusach || dummyOmerData.nusach}
                                showBracha={props.showBracha ?? dummyOmerData.showBracha}
                                showFullNusach={props.showFullNusach ?? dummyOmerData.showFullNusach}
                                showLeshemYichud={props.showLeshemYichud ?? dummyOmerData.showLeshemYichud}
                                showPasuk={props.showPasuk || dummyOmerData.showPasuk}
                                showAfterCount={props.showAfterCount ?? dummyOmerData.showAfterCount}
                                template={templateId || 'Elegant'}
                                title={title}
                                showTitle={showTitle}
                                bgColor={bgColor}
                            />
                        </AutoScaler>
                    </Box>
                );

            case 'HalachicTimesWidget':
                return (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: bgColor || 'white' }}>
                        <AutoScaler>
                            <HalachicTimesWidget
                                zmanim={{
                                    alotHaShachar: '05:15', sunrise: '06:30',
                                    sofZmanShmaMGA: '08:45', sofZmanShma: '09:15',
                                    sofZmanTfillaMGA: '09:45', sofZmanTfilla: '10:15',
                                    chatzot: '12:30', minchaGedola: '13:00',
                                    minchaKetana: '16:15', plagHaMincha: '17:30',
                                    sunset: '18:45', tzeit85deg: '19:20',
                                    chatzotNight: '00:30',
                                }}
                                hebrewDate={{ hebrewShort: "\u05D4\u05F3 \u05D1\u05E9\u05D1\u05D8", hd: 5, hm: 11 }}
                                visibleZmanim={props.visibleZmanim || null}
                                template={templateId || 'Classic'}
                                title={title}
                                showTitle={showTitle}
                                dispatch={() => {}}
                            />
                        </AutoScaler>
                    </Box>
                );

            default:
                return (
                    <Box sx={{ p: 2, textAlign: 'center', color: '#64748b', bgcolor: 'white' }}>
                        תצוגה מקדימה לא זמינה
                    </Box>
                );
        }
    };

    return (
        <WidgetPreview scale={scale}>
            {renderWidget()}
        </WidgetPreview>
    );
};

export default RealWidgetPreview;
